import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MegaMenuNav } from "@/components/MegaMenuNav";
import { Footer } from "@/components/Footer";
import {
  Heart,
  CheckCircle2,
  ArrowRight,
  Users,
  DollarSign,
  Handshake,
  Target,
} from "lucide-react";

export default function NonProfits() {
  const features = [
    {
      icon: DollarSign,
      title: "Non-Profit Pricing",
      description: "Special discounted rates for 501(c)(3) organizations with flexible payment terms and grant funding support.",
    },
    {
      icon: Users,
      title: "Community Impact",
      description: "Serve underserved populations with tools designed for diverse communities and varying skill levels.",
    },
    {
      icon: Handshake,
      title: "Mission-Aligned Partnership",
      description: "We're committed to your mission and provide ongoing support to maximize your impact.",
    },
    {
      icon: Target,
      title: "Outcome Tracking",
      description: "Demonstrate impact to donors and grantors with detailed success metrics and reporting.",
    },
  ];

  const benefits = [
    "Up to 50% discount for verified non-profits",
    "Grants management and reporting support",
    "Flexible payment plans and terms",
    "Pro bono hours for qualifying organizations",
    "Donor and impact reporting tools",
    "Community partner network access",
    "Training and onboarding assistance",
    "Success stories and case studies support",
  ];

  return (
    <>
      <Helmet>
        <title>Non-Profit Career Solutions | LoopBaan - Mission-Driven Employment Services</title>
        <meta
          name="description"
          content="Maximize impact with LoopBaan's non-profit career platform. Special pricing, community focus, mission alignment, and proven outcomes for 501(c)(3) organizations."
        />
      </Helmet>

      <MegaMenuNav />

      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
        {/* Hero Section */}
        <section className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-500/10 border border-teal-500/20 rounded-full mb-6">
              <Heart className="h-4 w-4 text-teal-400" />
              <span className="text-sm text-teal-400 font-medium">Non-Profit Organizations</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Maximize Your Impact
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">
                With Mission-Driven Tools
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Help more people in your community find meaningful employment with enterprise tools at non-profit prices. Special discounts for 501(c)(3) organizations.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white text-lg px-8 py-6">
                  Apply For Non-Profit Discount
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-6">
                  View Non-Profit Pricing
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">Special</div>
              <div className="text-gray-400 text-sm md:text-base">Non-Profit Pricing</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">Up to 50%</div>
              <div className="text-gray-400 text-sm md:text-base">Discount Available</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">Mission</div>
              <div className="text-gray-400 text-sm md:text-base">Impact Tracking</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-teal-400 mb-2">Flexible</div>
              <div className="text-gray-400 text-sm md:text-base">Payment Terms</div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="container mx-auto px-4 py-20">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Built For Social Impact
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Everything you need to serve your community and demonstrate impact
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {features.map((feature, idx) => (
              <Card key={idx} className="bg-slate-800/50 border-slate-700 p-8 hover:border-teal-500/50 transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-teal-500/10 rounded-lg">
                    <feature.icon className="h-8 w-8 text-teal-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-gray-400">{feature.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* Benefits */}
        <section className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-white text-center mb-12">
              Why Non-Profits Choose LoopBaan
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4">
                  <CheckCircle2 className="h-6 w-6 text-teal-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300 text-lg">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-teal-500/10 to-emerald-500/10 border border-teal-500/20 rounded-2xl p-12 text-center">
            <Heart className="h-16 w-16 text-teal-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to Amplify Your Impact?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Start using LoopBaan to help change lives in your community.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-teal-500 hover:bg-teal-600 text-white text-lg px-8 py-6">
                Get Started Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
}
